import { Component, ElementRef, Inject, OnInit, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MatDialog, MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { BehaviorSubject, Subject } from 'rxjs';
import { ErrorReportService } from 'src/services/error-report.service';
import { HttpService } from 'src/services/http.service';
import { CustomValidators } from 'src/validators';
import { ProcessListInterviewSessionDialogComponent } from '../process-list-interview-session-dialog/process-list-interview-session-dialog.component';
import * as moment from 'moment';
import { debounceTime } from 'rxjs/operators';
import { ViaCepService } from 'src/services/via-cep.service';

declare var google: any;

@Component({
  selector: 'app-process-list-interview-dialog',
  templateUrl: './process-list-interview-dialog.component.html',
  styleUrls: ['./process-list-interview-dialog.component.scss']
})
export class ProcessListInterviewDialogComponent implements OnInit {

  interviews: Array<any> = [];
  processListId: string;
  submiting: boolean = false;
  selectInterviewMode: boolean = false;
  formInterview: FormGroup;
  currentProcessList: any;
  isEditMode$: BehaviorSubject<boolean> = new BehaviorSubject(false);
  @ViewChild('autocomplete', { static: false }) autocompleteInput!: ElementRef;


  constructor(
    fb: FormBuilder,
    @Inject(MAT_DIALOG_DATA) data: any,
    private http: HttpService,
    private errorReport: ErrorReportService,
    private snackbar: MatSnackBar,
    private dialog: MatDialog,
    private dialogRef: MatDialogRef<ProcessListInterviewDialogComponent>,
    private viaCep: ViaCepService
  ) {

    this.processListId = data.processListId;
    this.currentProcessList = data.currentProcessList;
    this.formInterview = fb.group({
      id: [null],
      processList: [this.processListId, Validators.required],
      name: [null, Validators.required],
      dateStart: [null, [Validators.required, CustomValidators.minDate(moment(), 'YYYY-MM-DD')]],
      timeStart: [null, [Validators.required, CustomValidators.beforeDate('timeEnd', 'HH:mm'), CustomValidators.date('HH:mm')]],
      timeEnd: [null, [Validators.required, CustomValidators.afterDate('timeStart', 'HH:mm'), CustomValidators.date('HH:mm')]],
      nextDays: [true, Validators.required],
      contact: [null, Validators.required],
      address: [null, Validators.required],
      zipCode: [null, Validators.required],
      number: [null, Validators.required],
      type: [false],
      candidatesSessionDuration: [null, Validators.required]
    });
  }

  ngOnInit(): void {

    const subject = new Subject();
    subject.subscribe(
      (result: any) => {

        const { trigger, scope } = result;
        if (scope.indexOf('process_list:interview') > -1)
          this.submiting = trigger === 'pre';

      }
    )
    this.http.setLoadingIndicator(subject)

    this.getInterviews();

    this.isEditMode$.subscribe(isEditMode => {
      if (isEditMode) {
        // Remova validações no modo edição
        this.formInterview.get('dateStart')?.setValidators(null);
        this.formInterview.get('dateStart')?.setValidators([
          Validators.required
        ]);
      } else {
        // Adicione validações no modo criação
        this.formInterview.get('dateStart')?.setValidators([
          Validators.required,
          CustomValidators.minDate(moment(), 'YYYY-MM-DD')
        ]);
      }
      this.formInterview.get('dateStart')?.updateValueAndValidity();
    })

    this.formInterview.get('zipCode')
      .valueChanges
      .pipe(debounceTime(500))
      .subscribe(
        cep => {
          if (!this.formInterview.get('zipCode').invalid) {
            this.viaCep.getLocation(cep)
              .subscribe(
                data => {
                  const type = this.formInterview.get('type')?.value;
                  if (type === true) return;

                  if (data && !data.erro) {
                    const { bairro, localidade, logradouro, uf } = data;
                    const number = this.formInterview.get('number')?.value || '';
                    const fullAddress = `${logradouro}, ${number} - ${bairro}, ${localidade} - ${uf}, ${cep}, Brasil`;

                    this.formInterview.patchValue({
                      address: fullAddress,
                    })

                  } else {
                    alert('CEP inválido por favor tente novamente');
                    this.formInterview.patchValue({
                      zipCode: ''
                    });
                  }
                }
              )
          }
        }
      )
    
  }

  newInterview() {

    this.selectInterviewMode = true;
    this.isEditMode$.next(false);
    this.formInterview.reset({ nextDays: true, processList: this.processListId });
    //this.initAutocomplete();

  }

  getInterviews() {

    this.http.list('process_list:interview', { processList: this.processListId })
      .subscribe(
        (result: any) => {
          this.interviews = result.data;
        }
      )

  }

  public submitInterview() {

    const data = this.formInterview.getRawValue();
    const isValid = !this.formInterview.invalid;

    if (isValid) {

      const hours = moment(data.candidatesSessionDuration, 'HH:mm').hour();
      const minutes = moment(data.candidatesSessionDuration, 'HH:mm').minutes();
      data.candidatesSessionDuration = hours * 60 + minutes;

      const request$ = data.id
        ? this.http.save('process_list:interview_update', data)
        : this.http.save('process_list:interview', data);

      request$.subscribe(
        (result: any) => {
          if (result.type === 'OK') {
            this.selectInterviewMode = false;
            this.formInterview.reset({ processList: this.processListId });
            this.getInterviews();
          }
        },
        err => this.errorReport.showMessage(err)
      )

    }

  }

  deleteInterview(args, interview) {

    args.stopPropagation();

    if (!confirm(`Deseja realmente deletar essa entrevista desse processo seletivo`))
      return;

    const data = { id: interview.id };
    this.http.delete('process_list:interview', data)
      .subscribe(
        (result: any) => {
          if (result.type === 'OK')
            this.getInterviews();
          else if (result.type === 'FAIL')
            this.snackbar.open('Essa entrevista não pode ser excluida, já foi agendada por um ou mais candidatos', 'OK', { duration: 3000 })
          this.selectInterviewMode = false;
        },
        err => this.errorReport.showMessage(err)
      )

  }

  openSelectionTest(test) {

    if (this.formInterview.dirty
      && !confirm('O formulário em edição não foi salvo, deseja continuar?'))
      return;

    const [hours, mantissa] = (parseInt(test.candidates_session_duration) / 60).toFixed(2).split('.');
    const minutes = Math.floor((((parseInt(mantissa) / 100)) * 60));

    this.formInterview.markAsPristine();
    this.selectInterviewMode = true;
    this.isEditMode$.next(this.selectInterviewMode);
    const address = test.address;
      
    // Extrair o número (após a vírgula logo após o logradouro)
    const numberMatch = address.match(/,\s*(\d+)\s*-/);
    const number = numberMatch ? numberMatch[1] : null;

    // Extrair o CEP (padrão brasileiro: 5 dígitos + hífen + 3 dígitos)
    const cepMatch = address.match(/(\d{5}-?\d{3})/);
    const cep = cepMatch ? cepMatch[1] : null;

    this.formInterview.patchValue({
      id: test.id,
      processList: this.processListId,
      name: test.name,
      dateStart: test.date_start,
      timeStart: test.time_start,
      timeEnd: test.time_end,
      nextDays: test.next_days,
      contact: test.contact,
      address: test.address,
      zipCode: cep,
      number: number,
      candidatesSessionDuration: `${hours.padStart(2, '0')}:${minutes}`,
      type: test.type === 'true' || false,
    });
    //this.initAutocomplete();

  }

  openInterviewSession(interview) {

    this.dialogRef.close(true);
    const options = {
      disableClose: true,
      width: '900px',
      data: {
        processListId: this.processListId,
        interview,
        processList: this.currentProcessList
      }
    }
    this.dialog.open(ProcessListInterviewSessionDialogComponent, options)

  }

  onToggleChange(event: any): void {
    const isRemote = event.checked; // true para Remoto, false para Presencial

    if (isRemote) {
      // Ocultar campo e remover validação
      this.formInterview.get('address')?.clearValidators();
      this.formInterview.get('zipCode')?.clearValidators();
      this.formInterview.get('number')?.clearValidators();
    } else {
      // Mostrar campo e adicionar validação
      this.formInterview.get('address')?.addValidators(Validators.required);
      this.formInterview.get('zipCode')?.addValidators(Validators.required);
      this.formInterview.get('number')?.addValidators(Validators.required);
    }

    // Atualizar o estado do campo no formulário
    this.formInterview.get('address')?.updateValueAndValidity();
    this.formInterview.get('zipCode')?.updateValueAndValidity();
    this.formInterview.get('number')?.updateValueAndValidity();
    
  }

  formatTime(time: string): string {
    return time.substring(0, 5); // Retorna apenas os primeiros 5 caracteres (HH:mm)
  }

  // initAutocomplete() {
  //   setTimeout(() => {
      
  //     if (this.autocompleteInput?.nativeElement) {
  //       const autocomplete = new google.maps.places.Autocomplete(this.autocompleteInput.nativeElement, {
  //         types: ['geocode'], // Apenas endereços
  //         componentRestrictions: { country: 'BR' } // Restrito ao Brasil
  //       });

  //       autocomplete.addListener('place_changed', () => {
  //         const place = autocomplete.getPlace();
  
  //         if (place.formatted_address) {
  //           this.formInterview.get('address').setValue(place.formatted_address);
  //           //console.log('Endereço selecionado:', place.formatted_address);
  //         } else {
  //           console.warn('Endereço não encontrado!');
  //         }
  //       });

  //     } else {
  //       console.warn("Campo de endereço não está visível ainda!");
  //     }
  //   }, 500); // Aguarda um pequeno tempo para garantir a renderização
  // }

}
